import React, { useState } from 'react';
import BordResponsibleUserSearchDropdown from './BordResponsibleUserSearchDropdown/BordResponsibleUserSearchDropdown';
import PlusButton from '../../../assets/images/searchBar/plusButton';
import BordResponsibleUserPictures from './BordReponsibleUserPictures/BordResponsibleUserPictures';
import { IbackofficeUserData } from '../../../types/configuration.types';
import { IResponsibleUserData } from '../../../types/responsibleUser.types';
import useOnlyDevelopers from 'state/setOnlyDevelopers';

import './BordResponsibleUser.scss';

/**
 * Componente para gestionar responsables: muestra botón/foto, permite buscar y asignar responsables.
 * @param orderId Id de la orden.
 * @param allUsersList Lista de empleados disponibles.
 * @param logisticServiceId Id del servicio logístico.
 * @param responsibleUsersList Responsables asignados.
 * @param handleAssignResponsible Función para asignar un responsable.
 * @param handleDeleteResponsible Función para eliminar un responsable.
 * @param isLoading Booleano para indicar si se está cargando.
 * @param showResponsibleUsersSkeletonLoader Booleano para indicar si se debe mostrar el skeleton loader.
 * @param containerClassName prop para modificar estilos de contenedor, .
 * @param addIcon  props para modficar estilos del icono de añadir.
 */
const BordResponsibleUser: React.FC<BordResponsibleUserProps> = ({
  orderId,
  allUsersList,
  logisticServiceId,
  responsibleUsersList,
  handleAssignResponsible,
  handleDeleteResponsible,
  isLoading,
  showResponsibleUsersSkeletonLoader,
  addResponsiblesButtonClassName = '',
  plusButtonIconClassName = '',
  bordResponsibleUserSearchDropdownCustomClassName = ''
}) => {
  const { onlyDevelopers } = useOnlyDevelopers();
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false);

  const handleToggleSearchDropdown = () => setShowSearchDropdown(showSearchDropdown => !showSearchDropdown);

  if (showResponsibleUsersSkeletonLoader) return <div className="bordResponsibleUserSkeletonLoader animationLoader" />;

  return (
    <div className="bordResponsibleUser">
      <div
        className={`addResponsiblesButton${showSearchDropdown ? ' active' : ''} ${addResponsiblesButtonClassName}`}
        onClick={handleToggleSearchDropdown}
      >
        {!!responsibleUsersList.length && (
          <BordResponsibleUserPictures
            className="bordResponisibleUserPictureContainer"
            responsibleUsersList={responsibleUsersList}
            hideDropdown={showSearchDropdown}
          />
        )}

        <div
          className={`bordResponsibleUserTextWithButtonLayout ${
            !responsibleUsersList.length ? 'with8pxGap' : 'with2pxGap'
          }`}
        >
          {onlyDevelopers ? (
            'Agregar responsables'
          ) : (
            <span>{responsibleUsersList.length > 0 ? 'Agregar más responsables' : 'Agregar responsables'}</span>
          )}
          <PlusButton className={`plusButtonIcon ${plusButtonIconClassName}`} />
        </div>
      </div>

      {showSearchDropdown && (
        <BordResponsibleUserSearchDropdown
          className={bordResponsibleUserSearchDropdownCustomClassName}
          users={allUsersList || []}
          responsibleUsersList={responsibleUsersList}
          handleAssignResponsible={handleAssignResponsible}
          handleDeleteResponsible={handleDeleteResponsible}
          handleCloseDropdown={() => setShowSearchDropdown(false)}
          isLoading={isLoading}
          logisticServiceId={logisticServiceId}
          orderId={orderId}
        />
      )}
    </div>
  );
};

export default BordResponsibleUser;

export interface BordResponsibleUserProps {
  orderId?: number | null;
  isLoading?: boolean;
  allUsersList?: IbackofficeUserData[];
  logisticServiceId?: number | null;
  responsibleUsersList: IResponsibleUserData[];
  handleAssignResponsible: (user: IbackofficeUserData) => Promise<void>;
  handleDeleteResponsible: (responsibleId?: number, userName?: string) => Promise<void>;
  showResponsibleUsersSkeletonLoader?: boolean;
  containerClassName?: string;
  addIconClassName?: string;
  addResponsiblesButtonClassName?: string;
  plusButtonIconClassName?: string;
  bordResponsibleUserSearchDropdownCustomClassName?: string;
}
